/**
 * Program catalogue data.
 *
 * Everything here is real and comes from `viewer.availablePrograms`: program
 * identity, name, description, and the enrollment flags. The MOCK presentation
 * layer that used to be joined in — category, level, hours, cover images,
 * long-form overview and the whole course/module tree — has been removed.
 *
 * The fields it fed are still on `CatalogueProgram`, marked below, and every
 * consumer already treats them as optional. They are undefined for every
 * program today, so the surfaces built on them render their empty states: the
 * catalogue's category/level/duration filters have nothing to match, and the
 * enrolled-program card shows no courses. That is the honest state until a
 * backend provides them (tracked as BE debt in docs/fit-implementation-plan.md
 * §9).
 *
 * `courses` is the exception - it has a source now and is no longer read off
 * this type. The program detail page renders its structure from Savanna
 * instead: `Program.savannaCourses` gives the curriculum behind each course,
 * and `savannaCurriculum(id).flow` gives the track/block/activity tree with
 * per-activity paywall state. See `savannaCurriculum.ts` for that mapping,
 * including the free/partially-free/paid split below. `Course` and `Module`
 * stay here because both sources produce them.
 */
import { useMemo } from 'react';
import { useQuery } from '@apollo/client';

import {
  GetViewerAvailableProgramsDocument,
  GetViewerAvailableProgramsQuery,
} from '@sandtech-hub-monorepo/ehub-client';

// The card layout has nowhere to put an empty frame, so a program with no
// marketing thumbnail falls back to this.
import programCoverPlaceholder from '../assets/home-cover.webp';
import { plural } from '../utils/plural';

/** One program exactly as `GetViewerAvailablePrograms` returns it. */
export type AvailableProgram = NonNullable<
  GetViewerAvailableProgramsQuery['viewer']['availablePrograms']
>[number];

export type ProgramType = 'gsa' | 'degree';
export type ProgramLevel = 'Beginner' | 'Intermediate' | 'Advanced';
/**
 * How much of a module a learner without a subscription can open. Figma
 * 12150:55350 shows all three on the program structure list; the split itself
 * is not yet provided by any backend, and nothing enforces it server-side.
 */
export type ModuleAccess = 'free' | 'partially-free' | 'paid';

/**
 * What kind of thing an activity is. Savanna's `type` is a plain string
 * documented as `Concept`, `Project` or `EvaluationQuiz`; anything else maps to
 * `other` rather than being forced into one of the three, so a type added on
 * Savanna's side shows up unlabelled instead of mislabelled.
 */
export type ActivityKind = 'concept' | 'project' | 'quiz' | 'other';

/**
 * One thing a learner opens - the leaf of the INTERACTIVE tree.
 *
 * The informative tree stops at the module, because someone deciding whether
 * to join needs the shape of the course and its price, not a list of every
 * page in it. Once enrolled that reverses: the activity is the unit they work
 * through, and the only one Savanna gives a link to.
 */
export interface Activity {
  id: string;
  title: string;
  kind: ActivityKind;
  /** Behind the paywall, so a subscription is needed to open it. */
  paid: boolean;
  /** Undefined when the flow was read without a learner. See `Module.completed`. */
  completed?: boolean;
  /**
   * Savanna's `pathForLearner`, RELATIVE to the Savanna origin, and nullable
   * because Savanna does not promise one for every activity. A row without it
   * renders unlinked rather than pointing at the origin's root.
   */
  path?: string;
}

/**
 * One row of a course: Savanna's block.
 *
 * The block and not the track above it. A self-paced curriculum does not
 * display the track level at all, and the block is where the free/paid line
 * actually falls - it holds a handful of activities and some of them can be
 * paywalled while others are not, which is what makes `partially-free` a
 * state a row can really be in rather than a summary of rows beneath it.
 *
 * It is the LEAF of the informative tree, where `activities` goes unread and
 * the module states itself through `access` and `activityCount`. The
 * interactive tree opens it and renders them.
 */
export interface Module {
  id: string;
  title: string;
  /** Absent for anything sourced from Savanna, which records no duration. */
  hours?: number;
  /** Shown in place of hours when there are none. */
  activityCount?: number;
  access: ModuleAccess;
  /** The module's activities, in Savanna's order. Rendered once enrolled. */
  activities: Activity[];
  /**
   * Whether the learner has passed every activity in the module.
   *
   * Undefined rather than false when the flow was read without a learner - the
   * catalogue case. Savanna answers `isPassed: false` throughout for a flow it
   * resolved from a batch or from the curriculum template, so false there means
   * "nobody was asked about", not "not done yet".
   */
  completed?: boolean;
}

/**
 * A course's completion, counted in activities.
 *
 * The activity is the unit Savanna actually reports `isPassed` on, so counting
 * them states progress at the resolution it was measured. Counting modules
 * instead would round: a module is complete only when every activity in it is
 * passed, so a learner most of the way through a long module would show zero,
 * and the bar would jump a whole module at a time.
 *
 * Modules and blocks keep their own binary `completed` - a check mark is a
 * yes/no question - and this is the one place a proportion is shown.
 */
export interface CourseProgress {
  completedActivities: number;
  totalActivities: number;
}

export interface Course {
  id: string;
  /** 1-based position shown in the accordion badge. */
  index: number;
  title: string;
  /**
   * Savanna's curriculum id behind this course (`ClassLMSLocation.lmsCourseId`).
   * Only the interactive tree reads it, to send an activity's Start/Continue
   * link through `/ehub/authorize?context_curriculum_id=...` rather than
   * Savanna's raw origin. See `ActivityRow`.
   */
  curriculumId: string;
  /** Absent for anything sourced from Savanna, which records no duration. */
  hours?: number;
  /** Shown in place of hours when there are none. */
  activityCount?: number;
  /**
   * How the course's activities split across the paywall. An activity is
   * either free or paid - `partially-free` exists only at block level and
   * above, where a group can straddle the line - so these two are the whole
   * picture.
   *
   * Both are counted over the same traversal of the tracks rather than one
   * being subtracted from `activityCount`: that comes from Savanna's own
   * `totalActivities`, documented only as "total number of activities in the
   * flow", so it is not safe to assume it equals what the tracks contain.
   */
  freeActivityCount?: number;
  paidActivityCount?: number;
  /** Course-level lock — locked courses need a subscription to open. */
  locked: boolean;
  modules: Module[];
  /** Absent when the flow was read without a learner. See `Module.completed`. */
  progress?: CourseProgress;
}

/**
 * A program as the catalogue renders it: the query row itself, plus the
 * presentation fields no backend provides yet.
 *
 * It extends the generated type rather than restating it. There used to be a
 * `toCatalogueProgram` mapper building a parallel object — renaming `name` to
 * `title`, folding `isUniversityProgram` into a union, and carrying a dozen
 * fields that were always undefined. Every field it invented was one more
 * thing to keep in step with the query by hand. The four values it computed
 * are the four helpers below, applied where they are displayed.
 *
 * So `name`, `marketing`, `abbreviation`, `isEnrolled`, `canEnroll`,
 * `isGraduated` and the rest are read straight off the query, under the names
 * the schema gives them.
 */
export interface CatalogueProgram extends AvailableProgram {
  /** Not yet from the backend — card badge, and the category filter. */
  category?: string;
  /** Not yet from the backend — the level filter. */
  level?: ProgramLevel;
  /** Not yet from the backend — the duration filter and the "~N hrs" line. */
  hours?: number;
  /**
   * Not from this query — the detail page reads its structure from Savanna
   * (see `savannaCurriculum.ts`). Still here for the enrolled-program card,
   * which has no Savanna wiring yet.
   */
  courses?: Course[];
}

/**
 * The slice of a program the enrolled-program card renders. Narrow on purpose:
 * it is fed by a real enrollment, which has a name and a description and
 * nothing else.
 */
export interface EnrolledProgramSummary {
  title: string;
  description: string;
}

/**
 * The size of a course or module, for the line the accordion and the module
 * rows both show.
 *
 * Hours when something provides them, activity count otherwise: Savanna
 * records no duration anywhere in a curriculum flow, but it does say how many
 * activities a track holds, and an empty slot reads as missing data rather
 * than as data that does not exist. Undefined when neither is known, so the
 * caller renders nothing at all.
 */
export const sizeLabel = ({
  hours,
  activityCount,
}: Pick<Course, 'hours' | 'activityCount'>): string | undefined => {
  if (hours != null) {
    return `~${hours} ${plural(hours, 'hr', 'hrs')}`;
  }

  if (activityCount != null) {
    return `${activityCount} ${plural(activityCount, 'module', 'modules')}`;
  }

  return undefined;
};

export const PROGRAM_LEVELS: ProgramLevel[] = [
  'Beginner',
  'Intermediate',
  'Advanced',
];

/** Duration filter buckets (upper bound in hours; null = no upper bound). */
export const DURATION_BUCKETS: { label: string; max: number | null }[] = [
  { label: 'Under 100 hrs', max: 100 },
  { label: '100 – 200 hrs', max: 200 },
  { label: 'Over 200 hrs', max: null },
];

export interface UseProgramsResult {
  available: CatalogueProgram[];
  categories: string[];
  loading: boolean;
}

/*
 * The four values the catalogue shows that are not fields on Program. Each is
 * a fallback rather than a rename, which is why they are functions at the
 * point of display rather than columns on a mapped object.
 */

/** Marketing overrides the operational name when there is one. */
export const programTitle = (program: AvailableProgram): string =>
  program.marketing.title || program.name;

export const programDescription = (program: AvailableProgram): string =>
  program.marketing.subtitle || program.description || '';

/**
 * The program's own explanation of itself, as marketing wrote it.
 *
 * `sectionTitle` heads the section and `sectionSummary` is the body. Both are
 * nullable on `ProgramMarketing`, and they are read as a pair: a heading over
 * nothing is worse than no section, so the caller renders neither unless the
 * summary is there.
 *
 * The title falls back to "Overview" rather than to the program name, which
 * already heads the page - a section is being labelled here, not the program.
 */
export const programSectionTitle = (program: AvailableProgram): string =>
  program.marketing.sectionTitle || 'Overview';

export const programSectionSummary = (
  program: AvailableProgram
): string | undefined => program.marketing.sectionSummary || undefined;

/**
 * One cell of the hero stats strip: what is being stated, and marketing's
 * words for it.
 */
export interface ProgramStat {
  label: string;
  value: string;
}

/**
 * The hero stats strip, entirely as marketing authored it.
 *
 * Every value is free text a staff member typed into the program's Settings
 * tab - "6", "10 hrs / week", "36 (FREE 3 modules)", "Included with All
 * Access". They are copy, not counts: the units and the parentheticals are
 * part of the string, so nothing here can be summed, compared or recomputed.
 *
 * The strip used to derive its numbers from Savanna, which could only see the
 * first curriculum the page fetched and so had to qualify every figure with
 * "in Course 1". These fields describe the whole program, which is what the
 * strip claims to, so they replace that reading rather than backing it up.
 *
 * A cell with no copy is dropped rather than shown empty or defaulted: an
 * unwritten field means nobody has decided what this program's number is, and
 * inventing one would be worse than the shorter strip. `''` counts as
 * unwritten - saving the Settings tab with a blank input stores the empty
 * string, not null.
 */
export const programStats = (program: AvailableProgram): ProgramStat[] => {
  const { marketing } = program;

  return [
    { label: 'Courses', value: marketing.coursesSummary },
    { label: 'Time commitment', value: marketing.timeCommitment },
    { label: 'Modules', value: marketing.modulesSummary },
    { label: 'Certificate', value: marketing.certificateSummary },
  ].filter((stat): stat is ProgramStat => !!stat.value);
};

/** What the primary button on a program card is offering to do. */
export type ProgramCtaIntent =
  | 'start'
  | 'continue'
  | 'completed'
  | 'unavailable';

export interface ProgramCta {
  intent: ProgramCtaIntent;
  label: string;
  disabled: boolean;
}

/**
 * What a program's primary button should say and mean, given where the
 * learner already stands with it.
 *
 * The branch order matches the detail page's CTA chain, so the catalogue card
 * and the program page cannot disagree about the same program. Order is not
 * only style: `canEnroll` is already false once a learner is enrolled or has
 * graduated, so the two specific cases have to be read before the general one.
 *
 * `unavailable` is the residue - `canEnroll` false for some other reason, today
 * the one-program-at-a-time rule. The server sends only the boolean and a card
 * has no room for an explanation, so the button states the refusal and "View
 * details" leads to the page that explains it.
 *
 * COVERAGE IS DELIBERATELY NOT AN INPUT, and this is not an oversight to fix.
 * `stripeCoverage` is kept off `GetViewerAvailablePrograms` on purpose: that
 * document is shared with central-hub and ui-kit-site, where resolving it would
 * run the coverage join for every program in an ALX learner's catalogue. A
 * listing of N programs cannot ask; a single-program page can, which is why the
 * detail page splits these states further on `covered` and this does not.
 *
 * The enrolled learner loses nothing by that: `continue` sends them to
 * /learning, where the enrolled card carries the locks and the subscribe
 * upsell in context.
 */
export const programCta = (program: AvailableProgram): ProgramCta => {
  if (program.isEnrolled) {
    return { intent: 'continue', label: 'Continue Learning', disabled: false };
  }

  if (program.isGraduated) {
    return { intent: 'completed', label: 'Completed', disabled: true };
  }

  if (program.canEnroll) {
    return { intent: 'start', label: 'Start Now', disabled: false };
  }

  return { intent: 'unavailable', label: 'Start Now', disabled: true };
};

/** The card layout has nowhere to put an empty frame. */
export const programImage = (program: AvailableProgram): string =>
  program.marketing.thumbnail ?? programCoverPlaceholder;

/**
 * `isUniversityProgram` is the only Program-level discriminator the backend
 * has; this is it under the name the `?type=` url and the catalogue use.
 */
export const programType = (program: AvailableProgram): ProgramType =>
  program.isUniversityProgram ? 'degree' : 'gsa';

/**
 * The catalogue's programs for a product type. The list and the identity
 * fields are real; see the file header for what is still missing.
 */
export const usePrograms = (type: ProgramType): UseProgramsResult => {
  const { data, loading } = useQuery(GetViewerAvailableProgramsDocument, {
    fetchPolicy: 'cache-first',
  });

  // `isUniversityProgram` is the only Program-level discriminator; there is no
  // brand or product column to read instead. Filtering here rather than
  // server-side is tracked as TODO(PEDS-XXXX) on
  // GetViewerAvailableProgramPlans: the query takes no arguments, so the whole
  // catalogue crosses the wire to render one product type.
  const available = useMemo<CatalogueProgram[]>(
    () =>
      (data?.viewer?.availablePrograms ?? []).filter(
        (program) => program.isUniversityProgram === (type === 'degree')
      ),
    [data, type]
  );

  const categories = useMemo(
    () =>
      Array.from(
        new Set(
          available
            .map((program) => program.category)
            .filter((category): category is string => !!category)
        )
      ),
    [available]
  );

  // Every program here is open to enrolment: `availablePrograms` resolves
  // `db.program.findMany({ openToEnrollments: true })`, so an unlaunched one is
  // never returned by any learner-facing query. There is no "coming soon".
  return { available, categories, loading };
};
